import { getOperationName } from '@apollo/client/utilities'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { BACKEND_GRAPHQL_URL } from 'app/constants'
import { print } from 'graphql'

/**
 * Outcome of one analytics call: `retry` for a network error or a 5xx,
 * `drop` for a 4xx or a GraphQL error (the backend refuses the whole call
 * when one field is invalid, so a retry cannot help).
 */
export type SendResult = 'ok' | 'retry' | 'drop'

export interface SendOptions {
  /** `fetch` keepalive, for `pagehide` and a hidden tab. */
  keepalive?: boolean
}

/**
 * Runs one analytics call and never rejects: a call that throws, even before
 * returning a promise, counts as `retry`. NOTE: the call itself stays
 * synchronous, so a hidden-page flush starts its keepalive requests inside
 * the `pagehide` handler.
 */
export const settleSafely = (call: () => Promise<SendResult>) => {
  try {
    return call().catch((): SendResult => 'retry')
  } catch {
    return Promise.resolve<SendResult>('retry')
  }
}

const readGraphQLErrors = async (response: Response): Promise<unknown[]> => {
  try {
    const body: unknown = await response.json()
    const errors =
      typeof body === 'object' && body !== null && 'errors' in body ?
        body.errors
      : null

    return Array.isArray(errors) ? errors : []
  } catch {
    // NOTE: a 2xx without a readable body was still accepted by the gateway
    return []
  }
}

const toResult = async (
  response: Response,
  operationName: string | null
): Promise<SendResult> => {
  if (response.status >= 500) return 'retry'

  if (!response.ok) {
    console.warn(`analytics: ${operationName} refused, HTTP ${response.status}`)
    return 'drop'
  }

  const errors = await readGraphQLErrors(response)
  if (!errors.length) return 'ok'

  console.warn(`analytics: ${operationName} refused`, errors)
  return 'drop'
}

/**
 * Posts one analytics mutation to the gateway with the session cookies
 * (FE-321). Plain `fetch` rather than Apollo: `keepalive` is needed, and
 * analytics failures must not reach the error link and its toasts.
 *
 * Never throws. A refusal is logged with `console.warn`, which Sentry keeps
 * as a breadcrumb.
 */
export const sendAnalyticsOperation = async <TData, TVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  { keepalive = false }: SendOptions = {}
): Promise<SendResult> => {
  const operationName = getOperationName(document)

  try {
    const response = await fetch(BACKEND_GRAPHQL_URL, {
      method: 'POST',
      credentials: 'include',
      keepalive,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ operationName, query: print(document), variables })
    })

    return await toResult(response, operationName)
  } catch {
    return 'retry'
  }
}
